import { Circle, Spinner } from '@chakra-ui/react'
import { mdiAlertCircleOutline, mdiCheck, mdiFolderOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'

import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { HStack } from '@/components/ui/stack'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { asyncTimestampAtom, getTranslateFilesStatus } from '@/store/translate'
import { IDocTranslate } from '@/types/gigiChat/Conversation'

import AnswerDocTranslationMarkdown from './AnswerDocTranslationMarkdown'

const Container = ({ res, userPromptId }: { res: IDocTranslate; userPromptId: number | null }) => {
    const intervalId = useRef<ReturnType<typeof setTimeout> | null>(null)
    const dotsColor = useColorModeValue('rgba(84, 79, 64, 0.7)', 'rgba(255, 255, 255, 0.7)')
    const driveButtonColor = useColorModeValue('var(--chakra-colors-white)', 'var(--chakra-colors-black)')
    const [asyncTimestamp, setAsyncTimestamp] = useAtom<string>(asyncTimestampAtom)
    const previousSettings = useRef(userPromptId)

    const [translations, setTranslations] = useState<{
        files: Array<{
            name: string
            status: string
        }>
        language: Array<{
            languageEnglishName: string
            status: string
        }>
        documents: IDocTranslate
    }>()

    useEffect(() => {
        if (previousSettings.current && previousSettings.current !== userPromptId) {
            setAsyncTimestamp('')
        }
    }, [setAsyncTimestamp, userPromptId])

    useEffect(() => {
        if (translations?.documents.outputFolderLink) {
            setAsyncTimestamp('')
        }
    }, [setAsyncTimestamp, translations?.documents.outputFolderLink])

    useEffect(() => {
        async function getFileStatus() {
            if (!userPromptId) return null

            const documents = await getTranslateFilesStatus(userPromptId)
            if (documents) {
                const uniqueFiles = [
                    ...documents.documents.map((item) => {
                        return {
                            name: item.documentName,
                            status: item.status,
                        }
                    }),
                ]

                const uniqueLanguages = [...new Set(documents.details.map((item) => item.languageEnglishName))].map((lang) => {
                    const foundItem = documents.languages.find((item) => item.languageEnglishName === lang)
                    return {
                        languageEnglishName: lang,
                        status: foundItem ? foundItem.status : 'not ready',
                    }
                })

                setTranslations({
                    files: uniqueFiles,
                    language: uniqueLanguages,
                    documents: documents,
                })
            }

            return documents
        }
        function checkDocumentStatus() {
            if (translations) {
                const isDone = translations.documents.details.every((detail) => detail.status === 'error' || detail.status === 'complete')

                if (!isDone) {
                    if (!intervalId.current) {
                        intervalId.current = setInterval(async () => {
                            const res = await getFileStatus()
                            if (!res) {
                                intervalId.current && clearInterval(intervalId.current)
                                intervalId.current = null
                            }
                        }, 5000)
                    }
                } else if (isDone && intervalId.current) {
                    clearInterval(intervalId.current)
                    intervalId.current = null
                }
            }
        }
        if (!res.outputFolderLink && userPromptId) {
            checkDocumentStatus()
        }

        return () => {
            if (intervalId.current) {
                if (res.outputFolderLink) {
                    setAsyncTimestamp('')
                }
                clearInterval(intervalId.current)
                intervalId.current = null
            }
        }
    }, [res, setAsyncTimestamp, translations, userPromptId])

    useEffect(() => {
        const updateTranslations = (newData: IDocTranslate) => {
            const updateData = (data: IDocTranslate) => {
                const uniqueFiles = [
                    ...data.documents.map((item) => {
                        return {
                            name: item.documentName,
                            status: item.status,
                        }
                    }),
                ]

                const uniqueLanguages = [...new Set(data.details.map((item) => item.languageEnglishName))].map((lang) => {
                    const foundItem = data.languages.find((item) => item.languageEnglishName === lang)
                    return {
                        languageEnglishName: lang,
                        status: foundItem ? foundItem.status : 'not ready',
                    }
                })

                setTranslations({
                    files: uniqueFiles,
                    language: uniqueLanguages,
                    documents: res,
                })
            }
            updateData(newData)
        }

        updateTranslations(res)
    }, [res])

    const getDocumentsStatusIcon = (status: string) => {
        switch (status) {
            case 'not started':
                return (
                    <HStack gap={1} justify="center" align="center" mt="7px">
                        <Circle size="2px" bg={dotsColor} />
                        <Circle size="2px" bg={dotsColor} />
                        <Circle size="2px" bg={dotsColor} />
                    </HStack>
                )
            case 'started':
                return <Spinner w="14px" h="14px" color="var(--chakra-colors-gray-750)" animationDuration="0.85s" />
            case 'complete':
                return <Icon path={mdiCheck} size="16px" color="#448422" />
            case 'error':
                return <Icon path={mdiAlertCircleOutline} size="16px" color="#E40030" />
            default:
                return <Spinner w="14px" h="14px" color="var(--chakra-colors-gray-750)" animationDuration="0.85s" />
        }
    }

    const extractLanguagesWithError = (docName: string) => {
        return (
            translations?.documents.details
                .filter((doc) => doc.docName === docName && doc.status === 'error')
                .map((doc) => doc.languageEnglishName) || []
        )
    }

    return (
        <>
            {translations?.files.map((item, index) => {
                return (
                    <Flex key={item.name + index}>
                        <Box marginTop="10px">{getDocumentsStatusIcon(item.status)}</Box>
                        <Box
                            key={item.name + index}
                            fontSize="14px"
                            letterSpacing="0.25px"
                            mt="8px"
                            mb="8px"
                            ml="8px"
                            color={item.status === 'not started' ? dotsColor : ''}
                            w="95%"
                        >
                            <AnswerDocTranslationMarkdown answer={item.name} />
                            {extractLanguagesWithError(item.name).map((item, index) => {
                                return (
                                    <Flex alignItems="center" key={item + index} mt="8px">
                                        <Icon path={mdiAlertCircleOutline} size="16px" color="#E40030" />
                                        <Text ml="8px" fontSize="12px" letterSpacing="0.5px">
                                            Failed translating to {item}.
                                        </Text>
                                    </Flex>
                                )
                            })}
                        </Box>
                    </Flex>
                )
            })}
            {asyncTimestamp ? (
                <Box mt="-28px" textAlign="right" color={dotsColor} fontSize="12px" letterSpacing="0.5px">
                    <Text>{asyncTimestamp}</Text>
                </Box>
            ) : null}
            <Box divideX="2px" mt={asyncTimestamp ? '18px' : '8px'} mb="16px" />
            {translations?.documents.outputFolderLink ? (
                <Button
                    variant="secondary"
                    mr="18px"
                    onClick={() => window.open(translations.documents.outputFolderLink)}
                    backgroundColor={driveButtonColor}
                >
                    <Icon path={mdiFolderOutline} size="14px" />
                    <Text fontSize="12px" ml="8px" letterSpacing="1.5px">
                        OPEN DRIVE FOLDER
                    </Text>
                </Button>
            ) : (
                <Flex flexWrap="wrap">
                    <Box marginTop="3px" mr="8px">
                        <Icon path={mdiAlertCircleOutline} size="16px" color="#476FB5" />
                    </Box>
                    <Box width="90%">
                        <Text fontSize="16px" letterSpacing="0.5px">
                            Translating documents will take more than <b>5 minutes</b>.
                        </Text>
                        <Text fontSize="16px" letterSpacing="0.5px">
                            You can stay on this page or revisit it from your History once it's completed.
                        </Text>
                    </Box>
                </Flex>
            )}
        </>
    )
}

export default Container
